import React, {useContext, useEffect, useState, useCallback, Fragment, useMemo} from 'react'
import {useDispatch} from 'react-redux'
import {Link} from 'react-router-dom'
import {shallowEqual, useSelector} from 'react-redux'
import {RootState} from '../../../../setup'
import {fetchAll} from '../redux/MaterialRequisitionAction'
import {AbilityContext, Can} from '../../../rbac/Can'
import {KTSVG} from '../../../../_metronic/helpers'
import {
  MaterialRequisitionInitValues,
  MaterialRequisitionModel,
  MaterialRequisitionSearchModelInitValues,
  MaterialRequisitionTableModelInitValues,
} from '../models/MaterialRequisitionModel'
import {MaterialRequisitionCancelConfirmModal} from './MaterialRequisitionCancelConfirmModal'
import {MaterialRequisitionCompleteConfirmModal} from './MaterialRequisitionCompleteConfirmModal'
import {MaterialRequisitionDeleteConfirmModal} from './MaterialRequisitionDeleteConfirmModal'
import {ApprovalLegend} from '../../../components/ApprovalLegend'
import {Route, Breadcrumbs} from '../../../components/Breadcrumbs'
import {Table} from '../../../components/ReactTable'
import {formatDateTime} from '../../../helper/index'
import {Field, Form, Formik} from 'formik'
import * as Yup from 'yup'
import {DateRangePicker} from 'rsuite'
import 'rsuite/dist/rsuite.css'

type Props = {
  className?: string
}

export const defaultColumnSizing = {
  size: 150,
  minSize: 20,
  maxSize: Number.MAX_SAFE_INTEGER,
}

export const MaterialRequisitions: React.FC<Props> = ({className}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [materialRequisitionToDelete, setMRToDelete] = useState<MaterialRequisitionModel>(
    MaterialRequisitionInitValues
  )
  const dispatch = useDispatch()
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [materialRequisitionToCompleted, setMaterialRequisitionToCompleted] =
    useState<MaterialRequisitionModel>(MaterialRequisitionInitValues)

  const [materialRequisitionToCancelled, setMaterialRequisitionToCancelled] =
    useState<MaterialRequisitionModel>(MaterialRequisitionInitValues)

  const materialRequisitions: any = useSelector<RootState>(
    ({materialRequisition}) => materialRequisition.materialRequisitions,
    shallowEqual
  )

  const loading: any = useSelector<RootState>(
    ({materialRequisition}) => materialRequisition.loading,
    shallowEqual
  )
  const pages: any = useSelector<RootState>(
    ({materialRequisition}) => materialRequisition.pagination?.pages,
    shallowEqual
  )
  const ability = useContext(AbilityContext)

  const [searchParams, setSearchParams]: any = useState({})
  const [createdAt, setCreatedAt]: any = useState([])
  const [approvedAt, setApprovedAt]: any = useState([])
  const [show, setShow] = useState(false)
  const filterHandel = () => {
    setShow(!show)
  }

  useEffect(() => {
    dispatch(fetchAll(MaterialRequisitionTableModelInitValues))
  }, [dispatch])

  const fetchData = useCallback(
    ({pageSize, pageIndex, params, sortBy}: any) => {
      const queryParams = {
        page: pageIndex + 1,
        sortBy: sortBy[0]?.id || 'createdAt',
        sortOrder: sortBy[0]?.desc === false ? 'asc' : 'desc',
        search: params,
      }
      dispatch(fetchAll(queryParams))
    },
    [searchParams]
  )

  const columns = useMemo(
    () => [
      {
        Header: 'Created Date',
        accessor: 'requestedDate',
        Cell: (cellInfo: any) => {
          if (cellInfo?.row?.original?.requestedDate) {
            return formatDateTime(cellInfo?.row?.original?.requestedDate)
          } else {
            return '-'
          }
        },
        show: true,
      },
      {
        Header: 'Material Requisition',
        accessor: 'number',
        cellStyle: {textAlign: 'left'},
        Cell: (params: any) => (
          <Link to={`/material-requisition/${params?.row?.original?._id}`}>
            {params?.row?.original?.number}
          </Link>
        ),
      },
      {
        Header: 'Material Issuance',
        accessor: 'issuance?.number',
        disableSortBy: true,
        Cell: (params: any) => {
          const issuances = params?.row?.original?.materialIssuances
          return (
            <Fragment>
              {Array.isArray(issuances) &&
                issuances?.map((issuance: any, index: number) => (
                  <Fragment key={index}>
                    {ability.can('read', 'material-issuance') ? (
                      <Link to={`/material-issuance/${issuance?._id}`}>
                        {issuance?.number}&nbsp; &nbsp;
                        <br />
                      </Link>
                    ) : (
                      issuance?.number
                    )}
                  </Fragment>
                ))}
            </Fragment>
          )
        },
      },
      {
        Header: 'Outward',
        accessor: 'outward?.number',
        disableSortBy: true,
        cellStyle: {textAlign: 'left'},
        Cell: (params: any) => {
          const issuances = params?.row?.original?.materialIssuances
          return (
            <Fragment>
              {Array.isArray(issuances) &&
                issuances?.map((issuance: any, index: number) => (
                  <Fragment key={index}>
                    {ability.can('read', 'outward') ? (
                      <Link to={`/outward/${issuance?.outward?._id}`}>
                        {issuance?.outward?.number}
                        &nbsp; &nbsp;
                        <br />
                      </Link>
                    ) : (
                      issuance?.outward?.number
                    )}
                  </Fragment>
                ))}
            </Fragment>
          )
        },
      },

      {
        Header: 'Inward',
        accessor: 'inward?.number',
        disableSortBy: true,
        Cell: (params: any) => {
          const issuances = params?.row?.original?.materialIssuances
          return (
            <Fragment>
              {Array.isArray(issuances) &&
                issuances?.map((issuance: any, index: number) => (
                  <Fragment key={index}>
                    {ability.can('read', 'inward') ? (
                      <Link to={`/inward/${issuance?.outward?.inward?._id}`}>
                        {issuance?.outward?.inward?.number}
                        &nbsp; &nbsp;
                        <br />
                      </Link>
                    ) : (
                      issuance?.outward?.inward?.number
                    )}
                  </Fragment>
                ))}
            </Fragment>
          )
        },
      },
      {
        Header: 'Department',
        disableSortBy: true,
        accessor: 'department.name',
        show: true,
      },
      {
        Header: 'Location',
        disableSortBy: true,
        accessor: 'location.name',
        show: true,
      },
      {
        Header: 'Status',
        accessor: 'status',
        show: true,
      },
      {
        Header: 'Approved By',
        disableSortBy: true,
        accessor: 'approvedBy',
        Cell: (cellInfo: any) => {
          if (!cellInfo?.row?.original?.approvedBy) return '-'
          return cellInfo?.row?.original?.approvedBy?.fullName
        },
        show: true,
      },
      {
        Header: 'Approved Date',
        accessor: 'approvedAt',
        Cell: (params: any) => {
          if (!params?.row?.original?.approvedAt) return '-'
          return formatDateTime(params?.row?.original?.approvedAt)
        },
      },
      {
        Header: 'Actions',
        Cell: (params: any) => (
          <div className='action-btn-wrapper d-flex flex-shrink-0'>
            <Can do='approve' on='material-requisition'>
              <div className='action-btn-wrapper'>
                {params?.row?.original?.status === 'NEW' ? (
                  <Link to={`/material-requisition/${params?.row?.original?._id}/approve`}>
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                      data-bs-toggle='tooltip'
                      title='Approval'
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen026.svg'
                        className='svg-icon-primary svg-icon-3'
                      />
                    </button>
                  </Link>
                ) : params?.row?.original?.status === 'APPROVED' ||
                  params?.row?.original?.status === 'COMPLETED' ? (
                  <button
                    className='btn btn-icon btn-bg-light btn-active-color-success btn-sm me-1'
                    data-bs-toggle='tooltip'
                    title='Approved'
                  >
                    <KTSVG
                      path='/media/icons/duotune/general/gen026.svg'
                      className='svg-icon-success svg-icon-3'
                    />
                  </button>
                ) : (
                  <button
                    className='btn btn-icon btn-bg-light btn-active-color-default btn-sm me-1'
                    data-bs-toggle='tooltip'
                    title='Rejected'
                  >
                    <KTSVG
                      path='/media/icons/duotune/general/gen026.svg'
                      className='svg-icon-default svg-icon-3'
                    />
                  </button>
                )}
                <Can do='update' on='material-requisition'>
                  {params?.row?.original?.status === 'COMPLETED' ? (
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-success btn-sm me-1'
                      data-bs-toggle='tooltip'
                      title='Mark as completed'
                    >
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr085.svg'
                        className='svg-icon-success svg-icon-3'
                      />
                    </button>
                  ) : params?.row?.original?.status === 'REJECTED' ? (
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-secondary btn-sm me-1'
                      data-bs-toggle='tooltip'
                      title='Mark as completed'
                    >
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr085.svg'
                        className='svg-icon-secondary svg-icon-3'
                      />
                    </button>
                  ) : params?.row?.original?.status === 'CANCELLED' ? (
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-secondary btn-sm me-1'
                      data-bs-toggle='tooltip'
                      title='Mark as completed'
                    >
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr085.svg'
                        className='svg-icon-secondary svg-icon-3'
                      />
                    </button>
                  ) : (
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                      data-bs-toggle='tooltip'
                      title='Mark as completed'
                      onClick={() => {
                        setShowApproveDialog(true)
                        setMaterialRequisitionToCompleted(params?.row?.original)
                      }}
                    >
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr085.svg'
                        className='svg-icon-primary svg-icon-3'
                      />
                    </button>
                  )}
                </Can>

                <Can do='update' on='material-requisition'>
                  {params?.row?.original?.status === 'CANCELLED' ||
                  params?.row?.original?.items.some(
                    (item: any, index: number) => item.status === 'ISSUED'
                  ) ? (
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-secondary btn-sm me-1'
                      data-bs-toggle='tooltip'
                      title='Mark as cancelled'
                    >
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr015.svg'
                        className='svg-icon-gray-800
                svg-icon-3'
                      />
                    </button>
                  ) : params?.row?.original?.status === 'REJECTED' ? (
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-secondary btn-sm me-1'
                      data-bs-toggle='tooltip'
                      title='Mark as cancelled'
                    >
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr015.svg'
                        className='svg-icon-gray-800
                svg-icon-3'
                      />
                    </button>
                  ) : params?.row?.original?.status === 'COMPLETED' ? (
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-secondary btn-sm me-1'
                      data-bs-toggle='tooltip'
                      title='Mark as cancelled'
                    >
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr015.svg'
                        className='svg-icon-gray-800
                svg-icon-3'
                      />
                    </button>
                  ) : (
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                      data-bs-toggle='tooltip'
                      title='Mark as cancelled'
                      onClick={() => {
                        setShowCancelDialog(true)
                        setMaterialRequisitionToCancelled(params?.row?.original)
                      }}
                    >
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr015.svg'
                        className='svg-icon-gray-80 svg-icon-3'
                      />
                    </button>
                  )}
                </Can>
              </div>
            </Can>
            <Can do='delete' on='material-requisition'>
              {params?.row?.original?.status === 'NEW' && (
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                  data-bs-toggle='modal'
                  data-bs-target='#kt_modal_delete_app'
                  title='Delete Material Requisition'
                  onClick={() => {
                    setMRToDelete(params?.row?.original)
                    setShowDeleteDialog(true)
                  }}
                >
                  <KTSVG
                    path='/media/icons/duotune/general/gen027.svg'
                    className='m-3 svg-icon-3'
                  />
                </button>
              )}
            </Can>
          </div>
        ),
      },
    ],
    []
  )

  const routes: Route[] = [
    {name: 'Dashboard', url: '/dashboard'},
    {name: 'Material Requisitions List', url: '/material-requisition'},
  ]

  return (
    <>
      <MaterialRequisitionCompleteConfirmModal
        show={showApproveDialog}
        handleClose={() => {
          setShowApproveDialog(false)
        }}
        materialRequisition={materialRequisitionToCompleted}
      />

      <MaterialRequisitionCancelConfirmModal
        show={showCancelDialog}
        handleClose={() => {
          setShowCancelDialog(false)
        }}
        materialRequisition={materialRequisitionToCancelled}
      />

      <MaterialRequisitionDeleteConfirmModal
        show={showDeleteDialog}
        handleClose={() => {
          setShowDeleteDialog(false)
        }}
        materialRequisition={materialRequisitionToDelete}
      />

      <div className={`card card-custom ${className}`}>
        <Breadcrumbs routes={routes} />
        <div className='card-header border-0 pt-5'>
          <h1 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Material Requisitions List</span>
          </h1>
          <ApprovalLegend />
          <Can do='create' on='material-requisition'>
            <div
              className='card-toolbar'
              data-bs-toggle='tooltip'
              data-bs-placement='top'
              data-bs-trigger='hover'
              title='Add Material Requisition'
            >
              <Link to='/material-requisition/add' className='btn btn-sm btn-light-primary'>
                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                Add Material Requisition
              </Link>
            </div>
          </Can>
          <Can do='report' on='material-requisition'>
            <div
              className='card-toolbar'
              data-bs-toggle='tooltip'
              data-bs-placement='top'
              data-bs-trigger='hover'
              title='Material Requisition Report'
            >
              <Link to='/material-requisition/report' className='btn btn-sm btn-light-primary'>
                <KTSVG path='/media/icons/duotune/files/fil001.svg' className='svg-icon-3' />
                MR Report
              </Link>
            </div>
          </Can>
          <Can do='report' on='material-requisition'>
            <div
              className='card-toolbar'
              data-bs-toggle='tooltip'
              data-bs-placement='top'
              data-bs-trigger='hover'
              title='Material Requisition Department Report'
            >
              <Link
                to='/material-requisition/report/departments'
                className='btn btn-sm btn-light-primary'
              >
                <KTSVG path='/media/icons/duotune/files/fil001.svg' className='svg-icon-3' />
                MR Department Report
              </Link>
            </div>
          </Can>
          <Can do='report' on='material-requisition'>
            <div
              className='card-toolbar'
              data-bs-toggle='tooltip'
              data-bs-placement='top'
              data-bs-trigger='hover'
              title='Material Requisition Items Report'
            >
              <Link
                to='/material-requisition/report/items'
                className='btn btn-sm btn-light-primary'
              >
                <KTSVG path='/media/icons/duotune/files/fil001.svg' className='svg-icon-3' />
                MR Items Report
              </Link>
            </div>
          </Can>
        </div>
        <div className='card-body'>
          <div
            className={`d-flex align-items-center ${
              !show ? 'justify-content-end' : 'justify-content-between'
            }`}
          >
            <p className='my-0 me-3' style={{color: '#004B87', fontSize: '14px'}}>
              <b>Search Filters</b>
            </p>
            <span onClick={filterHandel}>
              <img src='/media/svg/icons/General/filter.svg' alt='' />
            </span>
          </div>
          {show ? (
            <Formik
              initialValues={MaterialRequisitionSearchModelInitValues}
              validationSchema={Yup.object({
                number: Yup.string(),
                status: Yup.string(),
                createdAt: Yup.array(Yup.string()),
                approvedAt: Yup.array(Yup.string()),
                approvedBy: Yup.string(),
              })}
              onSubmit={(values: any) => {
                if (values.status === 'Status') {
                  values.status = ''
                }

                setSearchParams(values)
              }}
            >
              {({values, setFieldValue}) => (
                <Form>
                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5'>
                    <div className='form-group col'>
                      <label htmlFor='createdAt' className='fw-bold'>
                        Created Date
                      </label>
                      <DateRangePicker
                        value={createdAt}
                        block
                        name='createdAt'
                        cleanable={true}
                        size='md'
                        appearance='default'
                        character='-'
                        showOneCalendar
                        placeholder='Select Date'
                        format='dd/MM/yyyy'
                        onClean={(value: any) => {
                          setFieldValue('createdAt', '')
                        }}
                        onChange={(value: any) => {
                          setFieldValue('createdAt', value)
                          setCreatedAt(value)
                        }}
                      />
                    </div>
                    <div className='form-group col'>
                      <label htmlFor='number' className='fw-bold'>
                        Material Requisition
                      </label>
                      <Field
                        name='number'
                        placeholder='Material Requisition Number'
                        className='form-control form-control-lg'
                      />
                    </div>
                  </div>

                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5 mt-1'>
                    <div className='form-group col'>
                      <label htmlFor='status' className='fw-bold'>
                        Status
                      </label>
                      <Field
                        as='select'
                        className='form-select form-select-md'
                        data-control='select2'
                        name='status'
                      >
                        <option>Status</option>
                        <option value='NEW'>NEW</option>
                        <option value='APPROVED'>APPROVED</option>
                        <option value='COMPLETED'>COMPLETED</option>
                        <option value='CANCELLED'>CANCELLED</option>
                        <option value='REJECTED'>REJECTED</option>
                      </Field>
                    </div>
                    <div className='form-group col'>
                      <label htmlFor='approvedAt' className='fw-bold'>
                        Approved Date
                      </label>
                      <DateRangePicker
                        value={approvedAt}
                        block
                        name='approvedAt'
                        cleanable={true}
                        size='md'
                        appearance='default'
                        character='-'
                        showOneCalendar
                        placeholder='Select Date'
                        format='dd/MM/yyyy'
                        onClean={(value: any) => {
                          setFieldValue('approvedAt', '')
                        }}
                        onChange={(value: any) => {
                          setFieldValue('approvedAt', value)
                          setApprovedAt(value)
                        }}
                      />
                    </div>
                  </div>
                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5 mt-1'>
                    <div className='form-group col'>
                      <label htmlFor='number' className='fw-bold'>
                        Approved By
                      </label>
                      <Field
                        name='approvedBy'
                        placeholder='Approved By'
                        className='form-control form-control-lg'
                      />
                    </div>
                  </div>
                  <div className='d-flex justify-content-end mt-10'>
                    <Link
                      to='/material-requisition'
                      className='btn btn-sm btn-light-primary me-3'
                      data-bs-toggle='tooltip'
                      data-bs-placement='top'
                      title='Cancel'
                      onClick={() => {
                        setSearchParams(MaterialRequisitionSearchModelInitValues)
                        setFieldValue('number', '')
                        setFieldValue('status', '')
                      }}
                    >
                      <span className='indicator-label'>Cancel</span>
                    </Link>

                    <button
                      type='submit'
                      className='btn btn-sm btn-primary'
                      data-bs-toggle='tooltip'
                      data-bs-placement='top'
                      title='Search'
                      id='liveToastBtn'
                    >
                      <span className='indicator-label'>Search</span>
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          ) : null}
          <div className='d-flex mt-5 '>
            <div className='react-table-wrapper' style={{width: '100%'}}>
              <Table
                columns={columns}
                data={materialRequisitions}
                params={searchParams}
                pageCount={pages}
                fetchData={fetchData}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
